import getInstallationMode from "./utils/getInstallationMode";
import getOS from "./utils/getOS";

interface ProductDetails {
    showAllBooks: boolean;
    showOnBoardingTour: boolean;
}

export interface AppConfig {
    Buildr: ProductDetails;
    ActiveTeach: ProductDetails;
}

export const APP_CONFIG: AppConfig = {
    Buildr: {
        showAllBooks: true,
        showOnBoardingTour: true,
    },
    ActiveTeach: {
        showAllBooks: false,
        showOnBoardingTour: false,
    },
};

export interface PreferenceItem {
    key: string;
    label: string;
    type: 'Object' | 'string' | 'number' | 'boolean';
    defaultValue: any;
    params: any;
    showOnUI: boolean;
    value: any;
}

export class UserPreference {
    private static instance: UserPreference;
    private items: PreferenceItem[] = [];
    private readonly STORAGE_KEY = 'user_preferences';

    private constructor() {
        let os = getOS();
        let installationMode = getInstallationMode() === 'unknown' ? process.env.REACT_APP_INSTALLATION_MODE : getInstallationMode()

        // Determine default and possible values for openARIModelUsing
        let openARIModelDefaultValue = 'Web Player';
        let openARIModelPossibleValues = ['Web Player'];

        if (os === 'android') {
            openARIModelDefaultValue = 'Android ARI Player';
            openARIModelPossibleValues = ['Android ARI Player', 'Web Player'];
        } else if (os === 'windows' && installationMode === 'desktop') {
            if (localStorage.getItem('INSTALLATION_PACKAGE') === 'server') {
                openARIModelDefaultValue = 'Web Player';
                openARIModelPossibleValues = ['Web Player'];
            }
            else {
                openARIModelDefaultValue = 'Windows Player';
                openARIModelPossibleValues = ['Windows Player', 'Web Player'];
            }
        }

        // Initialize default preferences
        this.items = [
            {
                key: 'openARIModelUsing',
                label: 'Open AR models using',
                type: 'string',
                defaultValue: openARIModelDefaultValue,
                params: {
                    possibleValues: openARIModelPossibleValues,
                },
                showOnUI: true,
                value: openARIModelDefaultValue,
            },
            {
                key: 'rememberLastOpenedClass',
                label: 'Remember last opened class',
                type: 'boolean',
                defaultValue: true,
                params: {
                    possibleValues: [true, false],
                },
                showOnUI: true,
                value: true,
            },
            {
                key: 'showDQB',
                label: 'Show DQB',
                type: 'string',
                defaultValue: 'When required',
                params: {
                    possibleValues: ['When required', 'Always'],
                },
                showOnUI: true,
                value: 'When required',
            },
        ];
    }

    public static getInstance(): UserPreference {
        if (!UserPreference.instance) {
            UserPreference.instance = new UserPreference();
        }
        return UserPreference.instance;
    }

    public async load(): Promise<void> {
        try {
            const storedPreferences = localStorage.getItem(this.STORAGE_KEY);
            if (storedPreferences) {
                const parsedPreferences = JSON.parse(storedPreferences);

                // Merge stored preferences with default values
                this.items = this.items.map((item) => {
                    const storedItem = parsedPreferences.find(
                        (p: PreferenceItem) => p.key === item.key
                    );
                    if (storedItem) {
                        return {
                            ...item,
                            value: storedItem.value,
                        };
                    }
                    return {
                        ...item,
                        value: item.defaultValue,
                    };
                });
            } else {
                // Initialize with default values if no stored preferences
                this.items = this.items.map((item) => ({
                    ...item,
                    value: item.defaultValue,
                }));
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
            // Fallback to defaults if there's an error
            this.items = this.items.map((item) => ({
                ...item,
                value: item.defaultValue,
            }));
        }
    }

    public getValue<T>(key: string): T | null {
        const item = this.items.find((item) => item.key === key);
        return item ? (item.value as T) : null;
    }

    public setValue<T>(key: string, value: T): boolean {
        const itemIndex = this.items.findIndex((item) => item.key === key);
        if (itemIndex === -1) {
            return false;
        }

        const item = this.items[itemIndex];

        // Validate value type
        if (typeof value !== item.type.toLowerCase()) {
            console.error(
                `Invalid value type. Expected ${item.type}, got ${typeof value}`
            );
            return false;
        }

        // Validate against possible values if defined
        if (
            item.params?.possibleValues &&
            !item.params.possibleValues.includes(value)
        ) {
            console.error(
                `Invalid value. Must be one of: ${item.params.possibleValues.join(', ')}`
            );
            return false;
        }

        // Update value
        this.items[itemIndex] = {
            ...item,
            value,
        };

        // Save to localStorage
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.items));
            return true;
        } catch (error) {
            console.error('Error saving preferences:', error);
            return false;
        }
    }

    public getUIPreferences(): PreferenceItem[] {
        return this.items.filter((item) => item.showOnUI);
    }

    public resetToDefaults(): void {
        this.items = this.items.map((item) => ({
            ...item,
            value: item.defaultValue,
        }));
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.items));
    }
}
